import { NavLink, Outlet } from 'react-router-dom';
import {
  Building2,
  Users,
  Banknote,
  BookOpen,
  GitBranch,
  UserCog,
  ShieldCheck,
  Database,
  AlertTriangle,
  KanbanSquare,
  Target,
} from 'lucide-react';
import { useRole } from '@/hooks/useRole';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const ITEMS: NavItem[] = [
  { to: '/parametres/organisation', label: t('parametres.nav.organisation'), icon: Building2 },
  { to: '/parametres/activites', label: 'Activités', icon: Target },
  { to: '/parametres/tiers', label: t('parametres.nav.tiers'), icon: Users },
  { to: '/parametres/tiers/doublons', label: 'Doublons tiers', icon: Users },
  { to: '/parametres/banques-caisses', label: t('parametres.nav.banques'), icon: Banknote },
  { to: '/parametres/plan-comptable', label: t('parametres.nav.plan'), icon: BookOpen },
  { to: '/parametres/workflows', label: t('parametres.nav.workflows'), icon: GitBranch },
  { to: '/parametres/utilisateurs', label: t('parametres.nav.utilisateurs'), icon: UserCog },
  { to: '/parametres/crm/pipelines', label: 'Pipelines CRM', icon: KanbanSquare },
  { to: '/parametres/audit', label: t('parametres.nav.audit'), icon: ShieldCheck },
];

const ADMIN_ITEMS: NavItem[] = [
  {
    to: '/parametres/backups',
    label: 'Backups & snapshots',
    icon: Database,
  },
  {
    to: '/parametres/admin',
    label: 'Sécurité — Zone Admin',
    icon: AlertTriangle,
  },
];

export function ParametresLayout(): JSX.Element {
  const { role } = useRole();
  const canView = usePermission('parametres.view');
  if (!canView) return <AccessDenied feature="parametres.view" />;
  const items = role === 'super_admin' ? [...ITEMS, ...ADMIN_ITEMS] : ITEMS;
  return (
    <div className="flex h-full">
      <aside className="scrollbar-thin w-60 shrink-0 overflow-y-auto border-r border-border bg-card p-3">
        <div className="mb-3 px-2 pb-3">
          <h2 className="text-sm font-semibold">{t('parametres.title')}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('parametres.subtitle')}</p>
        </div>
        <nav className="space-y-0.5">
          {items.map((item) => {
            const isAdmin = item.to === '/parametres/admin';
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
                    isActive
                      ? isAdmin
                        ? 'bg-destructive/10 font-medium text-destructive'
                        : 'bg-primary/10 font-medium text-primary'
                      : isAdmin
                        ? 'text-destructive/80 hover:bg-destructive/10 hover:text-destructive'
                        : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>
      <div className="scrollbar-thin flex-1 overflow-y-auto bg-secondary/20">
        <Outlet />
      </div>
    </div>
  );
}
